import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { useMutation } from "@tanstack/react-query";
import {
  AllCommunityModule,
  CellSelectionOptions,
  ColDef,
  GetRowIdParams,
  ModuleRegistry,
  RowSelectionOptions,
  Theme,
  themeQuartz
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { Stack, LinearProgress, Button, Typography } from "@mui/material";
import { getJobListQuery } from "../../Graphql/querries/jobQuery";
import { getAllJobsResponse, Job } from "../../Graphql/types/job";
import { fetchGraphQL } from "../../Graphql/utils";
import AlertBannerContext from "../../Contexts/alertBannerContext";
import ViewJdDetailButton from "./ViewJdDetailButton";
import Modal from "../Common/Modal/Modal";
import CreateJob from "./CreateJob";
import WithAuth from "../../Hoc/WithAuth";

ModuleRegistry.registerModules([AllCommunityModule]);

const JobList = () => {
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const { setAlert } = useContext(AlertBannerContext);
  const [rowData, setRowData] = useState<Job[]>([]);
  const [columnDefs, setColumnDefs] = useState<ColDef[]>();
  const containerStyle = useMemo(
    () => ({ width: "100%", height: "calc(100vh - 100px)" }),
    []
  );
  const gridStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
  const myTheme = themeQuartz.withParams({
    // color and style of border around selection
    rangeSelectionBorderColor: "rgb(193, 0, 97)",
    rangeSelectionBorderStyle: "dashed",
    // background color of selection - you can use a semi-transparent color
    // and it wil overlay on top of the existing cells
    rangeSelectionBackgroundColor: "rgb(255, 0, 128, 0.1)",
    // color used to indicate that data has been copied form the cell range
    rangeSelectionHighlightColor: "rgb(60, 188, 0, 0.3)"
  });

  const theme = useMemo<Theme | "legacy">(() => {
    return myTheme;
  }, []);

  const { mutate, isPending } = useMutation<getAllJobsResponse, Error>({
    mutationKey: ["getJobList"],
    mutationFn: () => fetchGraphQL(getJobListQuery, { input: {} }, setAlert),
    onSuccess: (response) => {
      setRowData(response.getAllJobs || []);
    },
    onError: () => {
      setRowData([]);
    }
  });

  const rowSelection: RowSelectionOptions = {
    mode: "singleRow",
    checkboxes: false
  };

  const cellSelection = useMemo<boolean | CellSelectionOptions>(() => {
    return { handle: { mode: "range" } };
  }, []);

  useEffect(() => {
    mutate();
    setColumnDefs([
      { field: "title", minWidth: 150 },
      { field: "postingStatus" },
      { field: "totalPay" },
      { field: "jobRole" },
      { field: "jobLevel" },
      { field: "jobType" },
      { field: "duration" },
      { field: "jobLocation" },
      {
        field: "actions",
        headerName: "Actions",
        cellRenderer: ViewJdDetailButton
      }
    ]);
  }, []);

  const getRowId = useCallback(
    (params: GetRowIdParams) => String(params.data.id),
    []
  );

  const defaultColDef = useMemo(() => {
    return {
      filter: true,
      floatingFilter: true,
      flex: 1,
      minWidth: 100,
      resizable: true
    };
  }, []);

  return (
    <div style={containerStyle}>
      {isPending && (
        <Stack width="100%" paddingBottom="1em" alignSelf="center">
          <LinearProgress />
        </Stack>
      )}
      <Stack
        style={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "row",
          width: "100%",
          padding: "1em"
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          Job Listing
        </Typography>
      </Stack>
      <Stack
        style={{
          display: "flex",
          justifyContent: "right",
          flexDirection: "row",
          width: "100%",
          padding: "2em",
          boxSizing: "border-box"
        }}
      >
        <Button variant="contained" onClick={handleOpen}>
          Create new job
        </Button>
      </Stack>
      <div style={gridStyle}>
        <AgGridReact
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowSelection={rowSelection}
          pagination
          paginationPageSize={10}
          paginationPageSizeSelector={[10, 25, 50]}
          theme={theme}
          cellSelection={cellSelection}
          getRowId={getRowId}
        />
      </div>
      {open && (
        <Modal title="Create a New Job" closeButton={handleClose}>
          <CreateJob />
        </Modal>
      )}
    </div>
  );
};

export default WithAuth(JobList);
